import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Select } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";

import { SYSTEM_ATTRIBUTE_PROPERTY } from "~/core/constants/attributes";
import { OBJECT_TYPE } from "~/core/constants/common";
import { useGlobalObjectSearch } from "~/core/hooks/objects/useGlobalSearch";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { SELECT_INPUT_FOCUS_DELAY } from "../../constants";
import { useGlobalNewOpportunityModalContext } from "../../context";
import { useGlobalCreateOpportunityStore } from "../../store";
import DDClearIcon from "../DDClearIcon";

import { AccountLabel } from "./AccountLabel";
import { AddAccount } from "./AddAccount";
import { DefaultDDRenderer } from "./DefaultDDRenderer";

export default function AccountSelect() {
  const { form, amountRef } = useGlobalNewOpportunityModalContext();
  const ref = useRef<any>(null);
  const [isOpen, setIsOpen] = useState(false);
  const {
    accounts,
    setAccounts,
    setSelectedAccount,
    selectedAccount,
    resetAccountSelection,
  } = useGlobalCreateOpportunityStore();
  const [searchQuery, setSearchQuery] = useState("");

  const isSearchEnabled = useMemo(() => {
    const hasSearchQuery = searchQuery && searchQuery?.trim() !== "";
    const hasAccounts = accounts.length > 0;

    return hasSearchQuery || hasAccounts;
  }, [searchQuery, accounts]);

  const { objectSearchData } = useGlobalObjectSearch({
    searchQuery: searchQuery,
    objectType: OBJECT_TYPE.ACCOUNT,
  });

  const options = useMemo(() => {
    return objectSearchData?.map(item => ({
      id: item.object_id,
      label: <AccountLabel item={item} />,
      active: selectedAccount?.id === item.object_id,
      searchableText: item.title || item.description,
      metadata: {
        description: item.description,
        title: item.title || item.description,
        image_url: item.image_url,
      },
    }));
  }, [objectSearchData, selectedAccount?.id]);

  useEffect(() => {
    const filteredOptions = options?.filter(
      option => option.id !== selectedAccount?.id,
    );
    setAccounts(
      selectedAccount
        ? [{ ...selectedAccount, active: true }, ...(filteredOptions || [])]
        : options || [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectSearchData]);

  const handleDropdownClose = () => {
    setIsOpen(false);
    // Focus the select after closing
    setTimeout(() => {
      ref.current?.focus();
    }, SELECT_INPUT_FOCUS_DELAY);
  };

  return (
    <Form.Item
      name={SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_ACCOUNT}
      label="Account"
    >
      <Select
        allowClear={{
          clearIcon: <DDClearIcon />,
        }}
        onClear={() => {
          resetAccountSelection();
          form?.resetFields([SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_ACCOUNT]);
        }}
        onFocus={() => {
          setIsOpen(true);
        }}
        placeholder="Select account"
        ref={ref}
        onDropdownVisibleChange={setIsOpen}
        open={isOpen}
        labelRender={e => {
          const account = selectedAccount;
          return <AccountLabel item={account?.metadata} />;
        }}
        suffixIcon={
          selectedAccount ? (
            <DDClearIcon asSuffix />
          ) : (
            <FontAwesomeIcon icon={faChevronDown} />
          )
        }
        dropdownRender={() => (
          <DefaultDDRenderer
            onCommandKeydown={e => {
              if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                handleDropdownClose();
              }
            }}
            items={accounts}
            onSearch={e => {
              setSearchQuery(e);
            }}
            onItemSelect={e => {
              form?.setFieldsValue({
                opportunity_account: e.id as string,
              });
              setIsOpen(false);
              setSelectedAccount(e);
              if (amountRef?.current) {
                amountRef.current.focus();
              }
            }}
            emptyContent={
              <AddAccount
                isSearchEnabled={isSearchEnabled}
                inputValue={searchQuery}
                setIsOpenDropdown={setIsOpen}
              />
            }
          />
        )}
      />
    </Form.Item>
  );
}
